import { useEffect, useMemo, useState } from "react";
import api, { API, formatErr } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import {
  ChevronLeft,
  ChevronRight,
  Image as ImageIcon,
  CheckCircle2,
  XCircle,
  CalendarOff,
  Trash2,
  Download,
  CalendarCheck,
  Camera,
  AlarmClock,
  Clock,
  FileSpreadsheet,
} from "lucide-react";
import Photos from "./Photos";
import LatePunch from "./LatePunch";
import Hours from "./Hours";

const MONTH_NAMES = [
  "", "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

function pad(n) {
  return n < 10 ? `0${n}` : `${n}`;
}

export default function Attendance() {
  const today = new Date();
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState("");
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [data, setData] = useState(null);
  const [activeTab, setActiveTab] = useState("attendance");

  // Edit dialog state
  const [editCell, setEditCell] = useState(null); // { worker, date, status }
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [checkInTime, setCheckInTime] = useState("09:00");
  const [checkOutTime, setCheckOutTime] = useState("18:00");
  const [selfieOpen, setSelfieOpen] = useState(false);
  const [selfie, setSelfie] = useState(null);
  // Search
  const [q, setQ] = useState("");

  const extractTime = (isoStr) => {
    if (!isoStr) return "";
    try {
      const parts = isoStr.split("T");
      if (parts.length > 1) {
        const timeParts = parts[1].split(":");
        if (timeParts.length >= 2) {
          return `${timeParts[0]}:${timeParts[1]}`;
        }
      }
    } catch (e) {
      // ignore
    }
    return "";
  };

  useEffect(() => {
    if (editCell) {
      setSelectedStatus(editCell.status || null);
      const proj = projects.find((p) => p.id === projectId);
      if (
        editCell.status === "present" &&
        selfie &&
        selfie.worker_id === editCell.worker.id &&
        selfie.date === editCell.date
      ) {
        setCheckInTime(extractTime(selfie.check_in_at) || proj?.start_time_hhmm || "09:00");
        setCheckOutTime(extractTime(selfie.check_out_at) || "18:00");
      } else {
        setCheckInTime(proj?.start_time_hhmm || "09:00");
        setCheckOutTime("18:00");
      }
    } else {
      setSelectedStatus(null);
      setCheckInTime("09:00");
      setCheckOutTime("18:00");
    }
  }, [editCell, projects, projectId, selfie]);

  useEffect(() => {
    api.get("/projects").then((r) => {
      setProjects(r.data);
      if (r.data.length && !projectId) setProjectId(r.data[0].id);
    });
  }, []);

  const load = async () => {
    if (!projectId) return;
    try {
      const r = await api.get(`/attendance/project/${projectId}`, {
        params: { year, month },
      });
      setData(r.data);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [projectId, year, month]);

  const days = data?.days_in_month || 0;
  const dateList = useMemo(() => {
    return Array.from({ length: days }, (_, i) =>
      `${year}-${pad(month)}-${pad(i + 1)}`
    );
  }, [days, year, month]);

  const isHoliday = (d) => data?.holidays?.includes(d);
  const isSunday = (d) => data?.sundays?.includes(d);
  const isManualHoliday = (d) => data?.manual_holidays?.includes(d);

  const cellState = (worker, d) => {
    const m = data?.matrix?.[worker.id]?.[d];
    const status = typeof m === "string" ? m : m?.status;
    const hours = typeof m === "object" ? m?.hours : null;
    const isSandwich = typeof m === "object" && m?.sandwich;
    const halfEnabled = data?.half_day_enabled !== false;
    const threshold = data?.half_day_threshold_hours ?? 6;
    if (status === "present") {
      if (halfEnabled && hours != null && hours < threshold) {
        return { cls: "att-cell half", txt: "0.5" };
      }
      return { cls: "att-cell present", txt: "P" };
    }
    if (status === "absent") {
      if (isSandwich) return { cls: "att-cell sandwich", txt: "A*" };
      return { cls: "att-cell absent", txt: "A" };
    }
    if (isHoliday(d)) return { cls: "att-cell holiday", txt: "H" };
    return { cls: "att-cell", txt: "·" };
  };

  const openEdit = (worker, date) => {
    const m = data?.matrix?.[worker.id]?.[date];
    const status = typeof m === "string" ? m : m?.status;
    if (status === "present") {
      openSelfie(worker.id, date);
      return;
    }
    setEditCell({ worker, date, status: status || null });
  };

  const setStatus = async (status, customTime = null, customCheckoutTime = null) => {
    if (!editCell) return;
    try {
      await api.post("/attendance/manual", {
        worker_id: editCell.worker.id,
        date: editCell.date,
        status, // "present" | "absent" | "clear"
        check_in_time: customTime,
        check_out_time: customCheckoutTime,
      });
      toast.success("Attendance updated");
      setEditCell(null);
      setSelectedStatus(null);
      load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    }
  };

  const toggleHolidayForDate = async (d) => {
    if (isSunday(d)) {
      toast.message("Sundays are auto-holidays");
      return;
    }
    try {
      if (isManualHoliday(d)) {
        await api.delete(`/holidays`, {
          params: { project_id: projectId, date: d },
        });
        toast.success("Holiday removed");
      } else {
        await api.post(`/holidays`, {
          project_id: projectId,
          date: d,
          reason: "Holiday",
        });
        toast.success("Marked as holiday");
      }
      setEditCell(null);
      load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    }
  };

  const openSelfie = async (workerId, date) => {
    try {
      const r = await api.get(`/attendance/selfie`, {
        params: { worker_id: workerId, date },
      });
      setSelfie(r.data);
      setSelfieOpen(true);
    } catch {
      toast.error("No record found");
    }
  };

  const navMonth = (delta) => {
    let m = month + delta;
    let y = year;
    if (m > 12) {
      m = 1;
      y += 1;
    }
    if (m < 1) {
      m = 12;
      y -= 1;
    }
    setMonth(m);
    setYear(y);
  };

  const downloadExcel = async () => {
    if (!projectId) return;
    try {
      const token = localStorage.getItem("itcot_token");
      const r = await fetch(
        `${API}/attendance/project/${projectId}/export?year=${year}&month=${month}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!r.ok) throw new Error("Download failed");
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `attendance_${year}-${String(month).padStart(2, "0")}.xlsx`;
      a.click();
    } catch (e) {
      toast.error("Failed to download Excel");
    }
  };

  const downloadPunchExcel = async () => {
    if (!projectId) return;
    try {
      const token = localStorage.getItem("itcot_token");
      const r = await fetch(
        `${API}/attendance/project/${projectId}/punch-export?year=${year}&month=${month}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!r.ok) throw new Error("Download failed");
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `punch_logs_${year}-${String(month).padStart(2, "0")}.xlsx`;
      a.click();
    } catch (e) {
      toast.error("Failed to download Punch Logs Excel");
    }
  };

  return (
    <div className="space-y-6" data-testid="attendance-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="font-display font-bold text-2xl text-[#1A3A5C]">
            Attendance Calendar
          </h2>
          <p className="text-sm text-[#475569]">
            Select a project to view monthly attendance
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            data-testid="download-excel-btn"
            onClick={downloadExcel}
            disabled={!projectId}
            className="border-[#22C55E] text-[#22C55E] hover:bg-[#22C55E] hover:text-white"
          >
            <Download className="w-4 h-4 mr-1" /> Download Excel
          </Button>
          <Button
            variant="outline"
            data-testid="download-punch-excel-btn"
            onClick={downloadPunchExcel}
            disabled={!projectId}
            className="border-[#0284C7] text-[#0284C7] hover:bg-[#0284C7] hover:text-white"
          >
            <FileSpreadsheet className="w-4 h-4 mr-1" /> Punch Logs
          </Button>
          <Select value={projectId} onValueChange={setProjectId}>
            <SelectTrigger className="w-72" data-testid="attendance-project-select">
              <SelectValue placeholder="Select project" />
            </SelectTrigger>
            <SelectContent>
              {projects.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.name} — {p.client_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Card className="p-4 flex items-center justify-between">
        <Button variant="outline" size="sm" onClick={() => navMonth(-1)}>
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <div className="flex items-center gap-3">
          <div className="font-display font-bold text-lg text-[#1A3A5C]">
            {new Date(year, month - 1, 1).toLocaleString("en", {
              month: "long",
              year: "numeric",
            })}
          </div>
          {data?.cl_cycle_start_month && data?.cl_cycle_end_month && (
            <span className="text-xs bg-amber-50 text-amber-900 border border-amber-200 px-2.5 py-1 rounded-md font-medium" data-testid="cl-cycle-badge">
              CL Cycle: {MONTH_NAMES[data.cl_cycle_start_month]} – {MONTH_NAMES[data.cl_cycle_end_month]}
            </span>
          )}
        </div>
        <Button variant="outline" size="sm" onClick={() => navMonth(1)}>
          <ChevronRight className="w-4 h-4" />
        </Button>
      </Card>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2.5 mb-6" data-testid="attendance-tabs">
        {[
          { id: "attendance", label: "Attendance", icon: CalendarCheck },
          { id: "photo", label: "Photo", icon: Camera },
          { id: "late", label: "Late Punch", icon: AlarmClock },
          { id: "hours", label: "Hours", icon: Clock },
        ].map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              data-testid={`tab-${tab.id}`}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-5 py-3 rounded-lg text-sm font-semibold transition-all duration-200 ${
                isActive
                  ? "bg-[#EA580C] text-white shadow-sm"
                  : "bg-white text-[#1A3A5C] border border-slate-200 hover:bg-slate-50 hover:border-slate-300 shadow-sm"
              }`}
            >
              <Icon className="w-4 h-4 shrink-0" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {activeTab === "attendance" && (
        <>
          <Card className="p-4">
            <div className="text-sm font-semibold text-[#1A3A5C] mb-2">Legend</div>
            <div className="flex flex-wrap gap-4 text-xs text-[#475569] items-center">
              <span className="flex items-center gap-1.5">
                <span className="att-cell present">P</span> Present
              </span>
              <span className="flex items-center gap-1.5">
                <span className="att-cell half">0.5</span> Half-day
              </span>
              <span className="flex items-center gap-1.5">
                <span className="att-cell absent">A</span> Absent
              </span>
              <span className="flex items-center gap-1.5">
                <span className="att-cell sandwich">A*</span> Sandwich Leave
              </span>
              <span className="flex items-center gap-1.5">
                <span className="att-cell holiday">H</span> Holiday / Sunday
              </span>
              <span className="flex items-center gap-1.5">
                <span className="att-cell">·</span> Not marked
              </span>
            </div>
            <p className="text-xs text-[#475569] mt-3">
              Click any cell to edit attendance (Present / Absent / Holiday / Clear).
            </p>
          </Card>

          {!data ? (
            <Card className="p-8 text-center text-[#475569]">
              {projects.length === 0
                ? "Add projects first to view attendance."
                : "Loading…"}
            </Card>
          ) : data.workers.length === 0 ? (
            <Card className="p-8 text-center text-[#475569]">
              No employees assigned to this project yet.
            </Card>
          ) : (
            <Card className="p-0 overflow-hidden">
              <div className="px-3 pt-3 pb-2 flex items-center justify-between gap-3 bg-slate-50/40 border-b">
                <Input
                  data-testid="attendance-search"
                  placeholder="Search employees…"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  className="max-w-xs"
                />
                <div className="text-xs text-[#475569]">
                  {(() => {
                    const ql = q.trim().toLowerCase();
                    const c = ql
                      ? data.workers.filter((w) =>
                          `${w.name} ${w.worker_id}`.toLowerCase().includes(ql)
                        ).length
                      : data.workers.length;
                    return `${c} / ${data.workers.length} employees`;
                  })()}
                </div>
              </div>
              <table className="w-full border-collapse table-fixed text-xs">
                <colgroup>
                  <col style={{ width: "120px" }} />
                  {dateList.map((d) => (
                    <col key={d} />
                  ))}
                  <col style={{ width: "42px" }} />
                  <col style={{ width: "42px" }} />
                  <col style={{ width: "42px" }} />
                  <col style={{ width: "42px" }} />
                  <col style={{ width: "42px" }} />
                  <col style={{ width: "46px" }} />
                </colgroup>
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-2 py-2 text-left text-xs font-semibold text-[#475569] sticky left-0 bg-slate-50 z-10">
                      Employee
                    </th>
                    {dateList.map((d) => {
                      const day = parseInt(d.split("-")[2]);
                      const sunday = isSunday(d);
                      return (
                        <th
                          key={d}
                          className={`px-0.5 py-2 text-[10px] font-semibold ${
                            sunday ? "text-[#A855F7]" : "text-[#475569]"
                          }`}
                        >
                          {day}
                        </th>
                      );
                    })}
                    <th className="px-1 py-2 text-[9px] font-semibold text-[#475569] bg-amber-50 leading-tight">CL<br/>Open</th>
                    <th className="px-1 py-2 text-[9px] font-semibold text-[#475569] bg-amber-50 leading-tight">CL<br/>Mo.</th>
                    <th className="px-1 py-2 text-[9px] font-semibold text-[#475569] bg-amber-50 leading-tight">CL<br/>Avl</th>
                    <th className="px-1 py-2 text-[9px] font-semibold text-[#475569] bg-amber-50 leading-tight">CL<br/>Bal</th>
                    <th className="px-1 py-2 text-[9px] font-semibold text-red-700 bg-red-50">LOP</th>
                    <th className="px-1 py-2 text-[9px] font-semibold text-[#15803D] bg-green-50">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const ql = q.trim().toLowerCase();
                    const filtered = ql
                      ? data.workers.filter((w) =>
                          `${w.name} ${w.worker_id}`.toLowerCase().includes(ql)
                        )
                      : data.workers;
                    if (filtered.length === 0) {
                      return (
                        <tr>
                          <td colSpan={dateList.length + 7} className="px-4 py-10 text-center text-[#475569]">
                            No employees match "{q}".
                          </td>
                        </tr>
                      );
                    }
                    return filtered.map((w) => {
                      const cl = data.cl_summary?.[w.id] || {};
                      return (
                        <tr
                          key={w.id}
                          data-testid={`att-row-${w.worker_id}`}
                          className="border-t hover:bg-slate-50"
                        >
                          <td className="px-2 py-1.5 sticky left-0 bg-white z-10 border-r">
                            <div className="font-semibold text-[#1A3A5C] text-xs truncate">
                              {w.name}
                            </div>
                            <div className="text-[10px] text-[#475569] font-mono">
                              {w.worker_id}
                            </div>
                          </td>
                          {dateList.map((d) => {
                            const st = cellState(w, d);
                            return (
                              <td key={d} className="px-0 py-1 text-center">
                                <span
                                  className={`${st.cls} clickable`}
                                  title={d}
                                  onClick={() => openEdit(w, d)}
                                >
                                  {st.txt}
                                </span>
                              </td>
                            );
                          })}
                          <td className="px-1 py-1.5 text-center text-xs bg-amber-50/40">{cl.opening ?? 0}</td>
                          <td className="px-1 py-1.5 text-center text-xs bg-amber-50/40">{cl.granted ?? 0}</td>
                          <td className="px-1 py-1.5 text-center text-xs bg-amber-50/40 font-semibold text-[#1A3A5C]">{cl.availed ?? 0}</td>
                          <td className="px-1 py-1.5 text-center text-xs bg-amber-50/40 font-semibold text-[#15803D]">{cl.balance ?? 0}</td>
                          <td className={`px-1 py-1.5 text-center text-xs font-bold ${cl.lop > 0 ? "bg-red-500 text-white" : "bg-red-50 text-red-700"}`}>
                            {cl.lop ?? 0}
                          </td>
                          <td className="px-1 py-1.5 text-center text-xs font-bold bg-green-50 text-[#15803D]">{cl.total_working ?? 0}</td>
                        </tr>
                      );
                    });
                  })()}
                </tbody>
              </table>
            </Card>
          )}
        </>
      )}

      {activeTab === "photo" && (
        <Photos projectId={projectId} year={year} month={month} />
      )}

      {activeTab === "late" && (
        <LatePunch projectId={projectId} year={year} month={month} projects={projects} />
      )}

      {activeTab === "hours" && (
        <Hours projectId={projectId} year={year} month={month} onCellClick={openEdit} />
      )}

      {/* Edit dialog */}
      <Dialog
        open={!!editCell}
        onOpenChange={(o) => !o && setEditCell(null)}
      >
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-display">
              Edit Attendance
            </DialogTitle>
          </DialogHeader>
          {editCell && (() => {
            const todayIST = new Date(
              new Date().toLocaleString("en-US", { timeZone: "Asia/Kolkata" })
            );
            const todayStr = `${todayIST.getFullYear()}-${String(
              todayIST.getMonth() + 1
            ).padStart(2, "0")}-${String(todayIST.getDate()).padStart(2, "0")}`;
            const isFuture = editCell.date > todayStr;
            return (
            <div className="space-y-3">
              <div className="bg-slate-50 rounded-lg p-3 text-sm">
                <div>
                  <span className="text-[#475569]">Employee: </span>
                  <strong className="text-[#1A3A5C]">{editCell.worker.name}</strong>
                  <span className="text-[#475569]"> ({editCell.worker.worker_id})</span>
                </div>
                <div>
                  <span className="text-[#475569]">Date: </span>
                  <strong className="text-[#1A3A5C]">{editCell.date}</strong>
                </div>
                <div>
                  <span className="text-[#475569]">Current: </span>
                  <strong className="text-[#1A3A5C]">
                    {editCell.status
                      ? editCell.status.toUpperCase()
                      : isHoliday(editCell.date)
                      ? "HOLIDAY"
                      : "—"}
                  </strong>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                {editCell.status === "present" && (
                  <Button
                    variant="outline"
                    onClick={() => openSelfie(editCell.worker.id, editCell.date)}
                    className="col-span-2"
                  >
                    <ImageIcon className="w-4 h-4 mr-1" /> View Selfie
                  </Button>
                )}
                <Button
                  data-testid="set-present-btn"
                  onClick={() => setSelectedStatus("present")}
                  disabled={isFuture}
                  className={`${
                    selectedStatus === "present"
                      ? "bg-[#16A34A] ring-2 ring-emerald-300"
                      : "bg-[#22C55E] hover:bg-[#16A34A]"
                  } text-white disabled:opacity-40`}
                >
                  <CheckCircle2 className="w-4 h-4 mr-1" /> Present
                </Button>
                <Button
                  data-testid="set-absent-btn"
                  onClick={() => setSelectedStatus("absent")}
                  disabled={isFuture}
                  className={`${
                    selectedStatus === "absent"
                      ? "bg-red-600 ring-2 ring-red-300"
                      : "bg-red-500 hover:bg-red-600"
                  } text-white disabled:opacity-40`}
                >
                  <XCircle className="w-4 h-4 mr-1" /> Absent
                </Button>
                <Button
                  data-testid="toggle-holiday-btn"
                  onClick={() => toggleHolidayForDate(editCell.date)}
                  className="bg-[#A855F7] hover:bg-[#7E22CE] text-white"
                  disabled={isSunday(editCell.date)}
                >
                  <CalendarOff className="w-4 h-4 mr-1" />
                  {isManualHoliday(editCell.date) ? "Unset Holiday" : "Holiday"}
                </Button>
                <Button
                  data-testid="clear-attendance-btn"
                  onClick={() => setSelectedStatus("clear")}
                  variant="outline"
                  className={selectedStatus === "clear" ? "border-slate-500 bg-slate-100" : ""}
                >
                  <Trash2 className="w-4 h-4 mr-1" /> Clear
                </Button>
              </div>

              {selectedStatus === "present" && (() => {
                const todayIST = new Date(
                  new Date().toLocaleString("en-US", { timeZone: "Asia/Kolkata" })
                );
                const todayStr = `${todayIST.getFullYear()}-${String(
                  todayIST.getMonth() + 1
                ).padStart(2, "0")}-${String(todayIST.getDate()).padStart(2, "0")}`;
                const isPrevious = editCell.date < todayStr;
                return (
                  <div className="space-y-3 mt-2 bg-slate-50 p-3 rounded-lg border border-slate-200">
                    <div className="space-y-1.5">
                      <label className="text-xs font-semibold text-[#1A3A5C] block">
                        Check-in Time
                      </label>
                      <input
                        type="time"
                        value={checkInTime}
                        onChange={(e) => setCheckInTime(e.target.value)}
                        className="w-full border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>
                    {isPrevious && (
                      <div className="space-y-1.5">
                        <label className="text-xs font-semibold text-[#1A3A5C] block">
                          Check-out Time
                        </label>
                        <input
                          type="time"
                          value={checkOutTime}
                          onChange={(e) => setCheckOutTime(e.target.value)}
                          className="w-full border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        />
                      </div>
                    )}
                  </div>
                );
              })()}

              {selectedStatus && (
                <Button
                  data-testid="submit-attendance-btn"
                  onClick={() => {
                    const todayIST = new Date(
                      new Date().toLocaleString("en-US", { timeZone: "Asia/Kolkata" })
                    );
                    const todayStr = `${todayIST.getFullYear()}-${String(
                      todayIST.getMonth() + 1
                    ).padStart(2, "0")}-${String(todayIST.getDate()).padStart(2, "0")}`;
                    const isPrevious = editCell.date < todayStr;

                    if (selectedStatus === "present" && isPrevious) {
                      if (!checkOutTime) {
                        toast.error("Checkout time is required for previous days");
                        return;
                      }
                      if (checkOutTime <= checkInTime) {
                        toast.error("Checkout time must be after checkin time");
                        return;
                      }
                    }
                    setStatus(
                      selectedStatus,
                      selectedStatus === "present" ? checkInTime : null,
                      selectedStatus === "present" ? checkOutTime : null
                    );
                  }}
                  className="w-full mt-3 bg-[#1A3A5C] hover:bg-[#2A8A8F] text-white"
                >
                  Submit Changes
                </Button>
              )}
              {isFuture && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                  Future date — only Holiday can be set.
                </p>
              )}
              {isSunday(editCell.date) && (
                <p className="text-xs text-[#A855F7]">
                  This is a Sunday — auto-holiday.
                </p>
              )}
            </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Selfie viewer */}
      <Dialog open={selfieOpen} onOpenChange={setSelfieOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display">
              Record — {selfie?.worker_code} — {selfie?.date}
            </DialogTitle>
          </DialogHeader>
          {selfie && (
            <div className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <div className="text-xs font-semibold text-[#475569] mb-1 flex items-center gap-2">
                    Check-in
                    {selfie.within_radius != null && (
                      <span
                        className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${
                          selfie.within_radius
                            ? "bg-green-100 text-green-800"
                            : "bg-amber-100 text-amber-800"
                        }`}
                      >
                        {selfie.within_radius ? "WITHIN RANGE" : "OUT OF RANGE"}
                      </span>
                    )}
                  </div>
                  {selfie.selfie_base64 ? (
                    <img
                      src={selfie.selfie_base64}
                      alt="Check-in selfie"
                      className="w-full rounded-lg border"
                    />
                  ) : (
                    <div className="bg-slate-50 rounded-lg border p-6 text-center text-sm text-[#475569]">
                      <ImageIcon className="w-6 h-6 mx-auto mb-1" />
                      No selfie
                    </div>
                  )}
                  {selfie.check_in_at && (
                    <div className="text-xs text-[#475569] mt-1">
                      {new Date(selfie.check_in_at).toLocaleString("en-IN", {
                        timeZone: "Asia/Kolkata",
                        dateStyle: "medium",
                        timeStyle: "short",
                      })}
                    </div>
                  )}
                </div>
                <div>
                  <div className="text-xs font-semibold text-[#475569] mb-1">
                    Check-out
                  </div>
                  {selfie.check_out_selfie_base64 ? (
                    <img
                      src={selfie.check_out_selfie_base64}
                      alt="Check-out selfie"
                      className="w-full rounded-lg border"
                    />
                  ) : (
                    <div className="bg-slate-50 rounded-lg border p-6 text-center text-sm text-[#475569]">
                      Not checked out yet
                    </div>
                  )}
                  {selfie.check_out_at && (
                    <div className="text-xs text-[#475569] mt-1">
                      {new Date(selfie.check_out_at).toLocaleString("en-IN", {
                        timeZone: "Asia/Kolkata",
                        dateStyle: "medium",
                        timeStyle: "short",
                      })}
                    </div>
                  )}
                </div>
              </div>
              <div className="bg-slate-50 rounded-lg p-3 text-xs text-[#475569] space-y-1">
                {selfie.gps_lat != null && (
                  <div>
                    GPS check-in: {selfie.gps_lat?.toFixed(5)},{" "}
                    {selfie.gps_lng?.toFixed(5)} ·{" "}
                    {Math.round(selfie.distance_m || 0)}m from site
                  </div>
                )}
                {selfie.check_out_gps_lat != null && (
                  <div>
                    GPS check-out: {selfie.check_out_gps_lat?.toFixed(5)},{" "}
                    {selfie.check_out_gps_lng?.toFixed(5)} ·{" "}
                    {Math.round(selfie.check_out_distance_m || 0)}m from site
                  </div>
                )}
                {selfie.working_hours != null && (
                  <div>
                    <strong className="text-[#1A3A5C]">
                      Working hours: {selfie.working_hours}
                    </strong>
                  </div>
                )}
                {selfie.manual_override && (
                  <div className="text-amber-700 font-semibold">
                    Manually edited by admin
                  </div>
                )}
              </div>
              <div className="flex justify-end">
                <Button
                  data-testid="edit-from-photos-btn"
                  variant="outline"
                  onClick={() => {
                    setSelfieOpen(false);
                    // Find worker & open edit dialog
                    const w = data.workers.find((x) => x.id === selfie.worker_id);
                    if (w) setEditCell({ worker: w, date: selfie.date, status: selfie.status });
                  }}
                >
                  <CalendarOff className="w-4 h-4 mr-1" /> Edit Record
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
